import { resolveAuthUserDisplayLabel } from '../../auth/domain/authUserDisplayLabels';
import { formatSaleShortRefSuffix } from './salePresentation';
import type { SalesReadPort } from '../domain/salesPorts';
import type { SaleDetail } from '../domain/salesTypes';
import { computeSalePaidPendingAmounts } from '../../shared/numeric/salePaymentAmounts';
import { resolveHasCustomerAllocations } from './resolveHasCustomerAllocations';

type EnrichPort = Pick<
  SalesReadPort,
  'countSalePaymentAllocations' | 'getSalePaymentTotals' | 'getCustomerName'
>;

export type EnrichSaleDetailOptions = {
  authUserLabels?: Record<string, string>;
  ownerAuthUserId?: string | null;
};

export async function enrichSaleDetail(
  detail: SaleDetail,
  readPort: EnrichPort,
  options?: EnrichSaleDetailOptions
): Promise<SaleDetail> {
  const labelById = options?.authUserLabels ?? {};
  const ownerAuthUserId = options?.ownerAuthUserId ?? null;
  const allocationCount = await readPort.countSalePaymentAllocations(detail.id);
  const totals = await readPort.getSalePaymentTotals(detail.id);
  const { paidAmount, pendingAmount } = computeSalePaidPendingAmounts(
    detail.total,
    detail.paymentStatus,
    totals.nonCustomerPaid,
    totals.customerAllocationPaid
  );

  const fiadoCustomerId =
    detail.fiadoCustomerId ??
    detail.payments.find((payment) => payment.customerId)?.customerId ??
    null;

  let fiadoCustomerName = detail.fiadoCustomerName ?? null;
  if (fiadoCustomerId && !fiadoCustomerName) {
    fiadoCustomerName = await readPort.getCustomerName(fiadoCustomerId);
  }

  return {
    ...detail,
    shortId:
      detail.shortId ??
      (detail.counterNumber != null && detail.counterNumber > 0
        ? String(detail.counterNumber)
        : formatSaleShortRefSuffix(detail.id) || null),
    cashierDisplayName:
      detail.cashierDisplayName ??
      resolveAuthUserDisplayLabel(detail.cashierUserId, labelById, ownerAuthUserId) ??
      (detail.cashierUserId == null ? 'Sin operador' : null),
    primaryStaffDisplayName:
      detail.primaryStaffDisplayName ??
      resolveAuthUserDisplayLabel(detail.primaryStaffUserId, labelById, ownerAuthUserId),
    voidedByDisplayName:
      detail.voidedByDisplayName ??
      resolveAuthUserDisplayLabel(detail.voidedByUserId, labelById, ownerAuthUserId),
    items: detail.items.map((item) => ({
      ...item,
      performedByDisplayName:
        item.performedByDisplayName ??
        resolveAuthUserDisplayLabel(item.performedByUserId, labelById, ownerAuthUserId),
      soldByDisplayName:
        item.soldByDisplayName ??
        resolveAuthUserDisplayLabel(item.soldByUserId, labelById, ownerAuthUserId),
    })),
    hasCustomerAllocations: resolveHasCustomerAllocations({
      allocationCount,
      detailHasCustomerAllocations: detail.hasCustomerAllocations,
      customerAllocationPaidFromTotals: totals.customerAllocationPaid,
      detailCustomerAllocationPaid: detail.customerAllocationPaid,
    }),
    fiadoCustomerId,
    fiadoCustomerName,
    paidAmount: detail.paidAmount ?? paidAmount,
    pendingAmount: detail.pendingAmount ?? pendingAmount,
  };
}
